import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Direction, Flex } from "@ntemarked/mittne-components/dist/Flex/Flex";

import ContentEditorField from "components/ContentEditor/ContentEditorField";
import { DangerButton } from "components/atoms/DangerButton";
import ContentPagePreview from "components/molecules/ContentPagePreview";
import FeedPreview from "components/molecules/FeedPreview";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import { ErrorWrapper } from "components/organisms/form/BaseField";
import Form from "components/organisms/form/Form";
import FormButton from "components/organisms/form/FormButton";
import SelectField from "components/organisms/form/SelectField";
import TextField from "components/organisms/form/TextField";
import TextareaField from "components/organisms/form/TextareaField";
import ToggleField from "components/organisms/form/ToggleField";
import { SingleInnholdQuery, useDeleteContentMutation, useSingleInnholdQuery, useUpsertContentMutation } from "pages/Content/FeedCards/Content.graphql-gen";
import { ContentSchema, ContentSegmentsEditor } from "pages/Content/FeedCards/CreateContentPage";
import { dateFormat } from "utils/Utils";

export default function EditContentPage() {
	const { contentId } = useParams<{ contentId: string }>();
	const navigate = useNavigate();
	const [upsertContent, { loading: upsertLoading, error: upsertError }] = useUpsertContentMutation();
	const [deleteContent, { loading: deleteLoading, error: deleteError }] = useDeleteContentMutation();
	const [previewState, setPreviewState] = useState<Partial<SingleInnholdQuery["singleContent"]>>({});
	const {
		data,
		loading: queryLoading,
		error: queryError,
	} = useSingleInnholdQuery({
		variables: {
			contentId: contentId || "",
		},
	});

	const [linkType, setLinkType] = useState<string>(data?.singleContent?.link?.type || "WEB");

	useEffect(() => {
		setLinkType(data?.singleContent?.link?.type || "WEB");
		setPreviewState(data?.singleContent);
	}, [data?.singleContent]);

	const error = upsertError ?? queryError ?? deleteError;
	const loading = upsertLoading || queryLoading || deleteLoading;

	return (
		<div>
			<MainLayoutPageHeader title="Rediger innhold" />
			<Flex gap="16px">
				<Form
					loading={loading}
					value={{
						...data?.singleContent,
						publishDate: data?.singleContent?.publishDate ? format(new Date(data.singleContent.publishDate), dateFormat) : undefined,
					}}
					key={data?.singleContent?.id}
					onSubmit={async (data) => {
						const { error } = await upsertContent({
							variables: {
								content: {
									id: contentId,
									...data,
									publishDate: new Date(data.publishDate).toISOString(),
								},
							},
						});

						!error && navigate(`/content`);
					}}
					schema={ContentSchema}
				>
					<Flex direction={Direction.column} gap="16px">
						<ToggleField name="published" label="Publisert" />
						<TextField
							name="title"
							label="Tittel"
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								const target = e.target;
								setPreviewState((oldState) => {
									return {
										...oldState,
										title: target.value || "",
									};
								});
							}}
						/>
						<TextareaField
							name="content"
							label="Innhold"
							onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
								const target = e.target;
								setPreviewState((oldState) => {
									return {
										...oldState,
										content: target.value || "",
									};
								});
							}}
						/>
						<TextField type="datetime-local" name="publishDate" label="Publiseringsdato" />

						<div>
							<TextField
								name="image.url"
								label="Bilde"
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
									const target = e.target;
									setPreviewState((oldState) => {
										return {
											...oldState,
											image: {
												...oldState?.image,
												url: target.value || "",
											},
										};
									});
								}}
							/>
							<TextField name="image.reference" type="hidden" value="" />
							<TextField name="image.provider" type="hidden" value="" />
						</div>

						<div>
							<SelectField
								name="link.type"
								label="Gå til"
								options={[
									{ value: "NO_LINK", label: "- vis kun innhold -" },
									{ value: "WEB", label: "Lenke" },
									{ value: "APP_CONTENT_PAGE", label: "Innholdsside i app" },
								]}
								onChange={(linkType) => setLinkType(linkType)}
							/>
							{linkType === "NO_LINK" && (
								<div style={{ display: "none" }}>
									<TextField name="link.url" type="hidden" />
									<TextField name="link.view" type="hidden" value="" />
									<TextField name="link.viewType" type="hidden" value="" />
								</div>
							)}
							{linkType === "WEB" && (
								<div>
									<TextField name="link.url" label="Lenke" />
									<TextField name="link.view" type="hidden" value="" />
									<TextField name="link.viewType" type="hidden" value="" />
								</div>
							)}
							{linkType === "APP_CONTENT_PAGE" && (
								<div style={{ display: "none" }}>
									<TextField name="link.url" value="" type="hidden" />
									<TextField name="link.view" type="hidden" value="content_page" />
									<TextField name="link.viewType" type="hidden" value="stack" />
								</div>
							)}
						</div>
						{linkType === "APP_CONTENT_PAGE" && (
							<Flex direction={Direction.column} gap="16px">
								<TextField
									name="pageTitle"
									label="Sidetittel"
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const target = e.target;
										setPreviewState((oldState) => {
											return {
												...oldState,
												pageTitle: target.value || "",
											};
										});
									}}
								/>
								<div>
									<TextField
										name="pageImage.url"
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
											const target = e.target;
											setPreviewState((oldState) => {
												return {
													...oldState,
													pageImage: {
														...oldState?.pageImage,
														url: target.value || "",
													},
												};
											});
										}}
										label="Bilde"
									/>
									<TextField name="pageImage.reference" type="hidden" value="" />
									<TextField name="pageImage.provider" type="hidden" value="" />
								</div>
								<ContentEditorField
									name="pageContent"
									label="Sideinnhold"
									onChange={(value) => {
										setPreviewState((oldState) => {
											return {
												...oldState,
												pageContent: value,
											};
										});
									}}
								/>
							</Flex>
						)}

						<ContentSegmentsEditor key={data?.singleContent?.id} />

						{error && <ErrorWrapper>{error.message}</ErrorWrapper>}
						<FormButton label="Lagre innhold" />
						<DangerButton
							label="Slett innhold"
							onClick={async () => {
								if (!window.confirm(`Sikker på at du ønsker å slette innholdet?`)) {
									return;
								}

								const { error } = await deleteContent({
									variables: { contentId: contentId || "" },
								});

								if (error) {
									return console.error(error);
								}

								navigate("/content");
							}}
						/>
					</Flex>
				</Form>
				<Flex direction={Direction.column} gap="16px" grow={0} shrink={0} style={{ width: "390px" }}>
					<FeedPreview
						title={previewState?.title || ""}
						content={previewState?.content || ""}
						date={previewState?.publishDate || new Date()}
						imageUrl={previewState?.image?.url || ""}
					/>
					{linkType === "APP_CONTENT_PAGE" && (
						<ContentPagePreview
							title={previewState?.pageTitle || ""}
							contentHtml={previewState?.pageContent?.html || ""}
							imageUrl={previewState?.pageImage?.url || ""}
						/>
					)}
					<div
						style={{
							display: "inline-block",
							width: "390px",
						}}
					/>
				</Flex>
			</Flex>
		</div>
	);
}
